"use client";

import { ErrorBoundary } from "@shared/components/ErrorBoundary";
import { cn } from "@ui/lib";
import { ChefHatIcon, MessageCircleIcon, PhoneCallIcon } from "lucide-react";
import {
	type CSSProperties,
	lazy,
	type ReactNode,
	Suspense,
	useEffect,
	useRef,
	useState,
} from "react";
import { track } from "../analytics";
import { type HeroAsk, LiveStore } from "./LiveStore";
import { PhoneScreen } from "./PhoneScreen";
import { CARD_SPOTS, type CardSpot } from "./spots";
import { HERO_SCENES, sceneDuration } from "./timeline";
import { useHeroClock } from "./use-hero-clock";

const HeroScene = lazy(() => import("./HeroScene"));

const STAGE_LABEL =
	"A phone shows a shopping assistant taking an order on a call in Saudi Arabic, turning a recipe into a basket, then answering a chat in Gulf Arabic, and the products it names fly into the basket.";

/** The pointer over the hero, -1 to 1 on each axis; read by the phone and the 3D cards. */
export interface HeroPointer {
	x: number;
	y: number;
}

function canRender3d(): boolean {
	// On phones the stage is the phone itself: the products would cover the chat.
	if (
		window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
		!window.matchMedia("(min-width: 640px)").matches
	) {
		return false;
	}
	const connection = (
		navigator as Navigator & {
			connection?: { saveData?: boolean };
		}
	).connection;
	if (connection?.saveData) {
		return false;
	}
	try {
		const canvas = document.createElement("canvas");
		return Boolean(
			canvas.getContext("webgl2") ?? canvas.getContext("webgl"),
		);
	} catch {
		return false;
	}
}

/**
 * A phone with the store's products floating around it. Until the visitor asks something, the
 * phone plays three scripted moments (a call, a recipe and a chat) and the products fly into it as the
 * assistant names them. Once they ask, the products fly in for good, the phone turns to face
 * them and its screen becomes the real store answering their question.
 *
 * The phone is HTML; once the page is idle, Three.js takes over the cards. Before that, and with
 * reduced motion, the same cards are flat.
 */
export function HeroStage({ ask }: { ask: HeroAsk | null }) {
	const stage = useRef<HTMLDivElement>(null);
	const phone = useRef<HTMLDivElement>(null);
	const pointer = useRef<HeroPointer>({ x: 0, y: 0 });
	const live = ask !== null;
	const liveSince = useRef<number | null>(null);
	const { frame, clock, run, playing, jump } = useHeroClock(stage, live);
	const [want3d, setWant3d] = useState(false);
	const [ready3d, setReady3d] = useState(false);
	const [onScreen, setOnScreen] = useState(true);

	// When the visitor first asked: the 3D cards and the phone's lean read it every frame.
	useEffect(() => {
		if (live && liveSince.current === null) {
			liveSince.current = performance.now() / 1000;
		}
	}, [live]);

	useEffect(() => {
		if (!canRender3d()) {
			return;
		}
		const start = () => setWant3d(true);
		if ("requestIdleCallback" in window) {
			const id = window.requestIdleCallback(start, { timeout: 2500 });
			return () => window.cancelIdleCallback(id);
		}
		const id = setTimeout(start, 1200);
		return () => clearTimeout(id);
	}, []);

	useEffect(() => {
		const el = stage.current;
		if (!el) {
			return;
		}
		const observer = new IntersectionObserver(([entry]) =>
			setOnScreen(Boolean(entry?.isIntersecting)),
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	// The phone leans toward the pointer, eased so it never snaps; live, it faces the visitor.
	useEffect(() => {
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
			return;
		}
		const target = { x: 0, y: 0 };
		const now = { x: 0, y: 0 };
		const lean = { x: 0, y: 0 };
		let raf = 0;
		const onMove = (event: PointerEvent) => {
			const rect = stage.current?.getBoundingClientRect();
			if (!rect) {
				return;
			}
			target.x = Math.max(
				-1,
				Math.min(1, ((event.clientX - rect.left) / rect.width) * 2 - 1),
			);
			target.y = Math.max(
				-1,
				Math.min(1, ((event.clientY - rect.top) / rect.height) * 2 - 1),
			);
		};
		const tick = () => {
			now.x += (target.x - now.x) * 0.06;
			now.y += (target.y - now.y) * 0.06;
			pointer.current = { x: now.x, y: now.y };
			const flat = liveSince.current !== null;
			lean.x += ((flat ? 0 : -12 + now.x * 9) - lean.x) * 0.08;
			lean.y += ((flat ? 0 : 4 - now.y * 6) - lean.y) * 0.08;
			phone.current?.style.setProperty("--ry", `${lean.x}deg`);
			phone.current?.style.setProperty("--rx", `${lean.y}deg`);
			raf = requestAnimationFrame(tick);
		};
		window.addEventListener("pointermove", onMove, { passive: true });
		raf = requestAnimationFrame(tick);
		return () => {
			window.removeEventListener("pointermove", onMove);
			cancelAnimationFrame(raf);
		};
	}, []);

	return (
		<div className="flex h-full flex-col">
			<SceneTabs
				active={frame.scene}
				run={run}
				playing={playing}
				hidden={live}
				onPick={(scene) => {
					jump(scene);
					track("hero_scene_selected", {
						scene: HERO_SCENES[scene]?.tab,
					});
				}}
			/>
			<div
				ref={stage}
				className="relative mx-auto w-full max-w-[880px] flex-1"
				// Until the visitor asks, the stage is a picture; after, the store inside it is real.
				{...(live ? {} : { role: "img", "aria-label": STAGE_LABEL })}
			>
				<div
					aria-hidden
					className="pointer-events-none absolute bottom-[4%] left-1/2 h-10 w-[300px] -translate-x-1/2 rounded-[50%] bg-[radial-gradient(closest-side,oklch(0.2_0.004_270/0.18),transparent)]"
				/>
				<div
					aria-hidden
					className={cn(
						"absolute inset-0 transition-opacity duration-700",
						(ready3d || live) && "opacity-0",
					)}
				>
					{CARD_SPOTS.map((spot, i) => (
						<FlatCard key={spot.product.id} spot={spot} index={i} />
					))}
				</div>

				<div className="absolute inset-0 grid place-items-center [perspective:1800px]">
					<div className={cn(!live && "mk-float")}>
						<div
							ref={phone}
							className="origin-center scale-[0.86] transform-3d [--rx:4deg] [--ry:-12deg] [transform:rotateX(var(--rx))_rotateY(var(--ry))] sm:scale-[0.92] lg:scale-100"
						>
							<PhoneFrame>
								{ask ? (
									<LiveStore ask={ask} />
								) : (
									<PhoneScreen frame={frame} />
								)}
							</PhoneFrame>
						</div>
					</div>
				</div>

				{want3d ? (
					// Some browsers expose WebGL that Three.js still can't use (blocklisted GPUs,
					// virtual machines): the 3D layer then throws. It must never take the page with
					// it, so it fails alone and the flat cards stay.
					<ErrorBoundary
						fallbackRender={() => null}
						onError={() => setReady3d(false)}
					>
						<Suspense fallback={null}>
							<HeroScene
								clock={clock}
								pointer={pointer}
								liveSince={liveSince}
								active={onScreen}
								onReady={() => setReady3d(true)}
							/>
						</Suspense>
					</ErrorBoundary>
				) : null}
			</div>
		</div>
	);
}

const TAB_ICONS = [PhoneCallIcon, ChefHatIcon, MessageCircleIcon];

/**
 * The three moments as tabs, so a visitor sees there is more than one and can pick it. The
 * playing one fills a bar for as long as it lasts.
 */
function SceneTabs({
	active,
	run,
	playing,
	hidden,
	onPick,
}: {
	active: number;
	run: number;
	playing: boolean;
	hidden: boolean;
	onPick: (scene: number) => void;
}) {
	return (
		<div
			className={cn(
				"relative z-10 mx-auto mb-3 flex w-fit max-w-full gap-1 rounded-full bg-(--mk-mist) p-1 transition-opacity duration-(--duration-modal)",
				hidden && "pointer-events-none opacity-0",
			)}
			aria-hidden={hidden || undefined}
		>
			{HERO_SCENES.map((scene, i) => {
				const Icon = TAB_ICONS[i] ?? PhoneCallIcon;
				const on = i === active;
				return (
					<button
						key={scene.tab}
						type="button"
						aria-pressed={on}
						onClick={() => onPick(i)}
						className={cn(
							"relative flex h-9 items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-full px-2.5 text-[12.5px] outline-none transition-[background-color,color,box-shadow] duration-(--duration-hover) focus-visible:ring-2 focus-visible:ring-(--mk-signal) sm:px-4 sm:text-[14px]",
							on
								? "bg-(--mk-white) text-(--mk-ink) shadow-[0_1px_3px_oklch(0.2_0.004_270/0.12)]"
								: "text-(--mk-muted) hover:text-(--mk-ink)",
						)}
					>
						<Icon
							className="hidden size-3.5 sm:block"
							aria-hidden
						/>
						{scene.tab}
						{on ? (
							<span
								key={run}
								aria-hidden
								className="mk-progress absolute inset-x-3 bottom-1 h-[2px] rounded-full bg-(--mk-ink)/70 sm:inset-x-4"
								style={
									{
										"--mk-duration": `${sceneDuration(i)}ms`,
										animationPlayState: playing
											? "running"
											: "paused",
									} as CSSProperties
								}
							/>
						) : null}
					</button>
				);
			})}
		</div>
	);
}

function PhoneFrame({ children }: { children: ReactNode }) {
	return (
		<div className="relative h-[652px] w-[318px] rounded-[54px] bg-[linear-gradient(145deg,oklch(0.34_0.004_270),oklch(0.15_0.003_270)_45%,oklch(0.28_0.004_270))] p-[9px] shadow-[0_40px_80px_-30px_oklch(0.2_0.004_270/0.35),0_24px_48px_-28px_oklch(0.2_0.004_270/0.4),inset_0_0_0_1.5px_oklch(0.45_0.004_270/0.6)]">
			<div className="relative h-full overflow-hidden rounded-[45px] bg-(--mk-white)">
				{children}
				<span className="pointer-events-none absolute top-2.5 left-1/2 h-[26px] w-[92px] -translate-x-1/2 rounded-full bg-black" />
			</div>
		</div>
	);
}

function FlatCard({ spot, index }: { spot: CardSpot; index: number }) {
	const size = Math.round(132 * (1 - spot.depth * 0.32));
	return (
		<div
			className="absolute hidden -translate-x-1/2 -translate-y-1/2 sm:block"
			style={{ left: `${spot.x * 100}%`, top: `${spot.y * 100}%` }}
		>
			<div
				className="mk-float"
				style={{ animationDelay: `${index * -0.9}s` }}
			>
				<img
					// Hidden on phones: lazy, so a phone never downloads them.
					loading="lazy"
					decoding="async"
					src={spot.product.thumb}
					srcSet={`${spot.product.thumb} 256w, ${spot.product.image} 512w`}
					sizes={`${size}px`}
					alt=""
					width={size}
					height={size}
					// Fixed size: the base img max-width would squeeze cards near the stage's edge.
					className="max-w-none drop-shadow-[0_22px_18px_oklch(0.2_0.004_270/0.22)]"
					style={{
						width: size,
						height: size,
						transform: `rotate(${spot.tilt}deg)`,
						filter: spot.depth > 0.4 ? "blur(0.5px)" : undefined,
						opacity: 1 - spot.depth * 0.25,
					}}
				/>
			</div>
		</div>
	);
}
